import {
    memo,
    type PropsWithChildren,
    type ReactNode,
    useCallback,
} from 'react';

import type { ModalProps as AntModalProps } from 'antd';
import { Modal as AntModal } from 'antd';
import classNames from 'classnames';

import { useMobileMediaQuery } from '@/shared/lib/hooks';
import { Button } from '@/shared/ui/button';

import cls from './modal.module.css';

interface ModalAction {
    callback?: () => void;
    label: string;
    isLoading?: boolean;
    disabled?: boolean;
    disabledTooltipText?: string;
    variant?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
}

export interface ModalProps extends PropsWithChildren {
    content: ReactNode;
    isOpen: boolean;
    modalProps?: Partial<AntModalProps>;
    desktopWidth?: number | string;
    onClose: () => void;
    primaryAction?: ModalAction;
    secondaryAction?: ModalAction;
    title?: ReactNode;
}

export const Modal = memo(
    ({
        content,
        isOpen,
        modalProps,
        onClose,
        primaryAction,
        desktopWidth,
        secondaryAction,
        title,
    }: ModalProps) => {
        const isMobile = useMobileMediaQuery();

        const handleClose = useCallback(() => {
            onClose();
        }, [onClose]);

        const footer =
            primaryAction || secondaryAction ? (
                <div className={cls.footer}>
                    {secondaryAction && (
                        <Button
                            key="secondary"
                            {...(secondaryAction.disabled !== undefined && {
                                disabled: secondaryAction.disabled,
                            })}
                            {...(secondaryAction.disabledTooltipText && {
                                disabledTooltipText:
                                    secondaryAction.disabledTooltipText,
                            })}
                            isLoading={secondaryAction.isLoading ?? false}
                            onClick={secondaryAction.callback ?? handleClose}
                            title={secondaryAction.label}
                            variant="secondary"
                        />
                    )}
                    {primaryAction && (
                        <Button
                            key="primary"
                            {...(primaryAction.disabled !== undefined && {
                                disabled: primaryAction.disabled,
                            })}
                            {...(primaryAction.disabledTooltipText && {
                                disabledTooltipText:
                                    primaryAction.disabledTooltipText,
                            })}
                            isLoading={primaryAction.isLoading ?? false}
                            onClick={primaryAction.callback}
                            title={primaryAction.label}
                            variant={primaryAction.variant ?? 'primary'}
                        />
                    )}
                </div>
            ) : null;

        return (
            <AntModal
                centered
                className="algotech-modal"
                footer={footer}
                maskClosable
                onCancel={handleClose}
                open={isOpen}
                title={title}
                width={isMobile ? '100%' : desktopWidth ?? 600}
                wrapClassName={classNames(cls.root, {
                    [cls.noTitle]: !title,
                })}
                zIndex={1200}
                {...modalProps}
            >
                {content}
            </AntModal>
        );
    },
);

Modal.displayName = 'Modal';
